/**
 * TreatX Admin — Commission Management
 *
 * PXC-B.3 refactor (FE-only, additive):
 *
 *   The legacy raw rule-engine console is preserved untouched under the
 *   "Advanced" tab. The new "Simplified" tab (default) renders a calm
 *   commercial matrix: one row per package, two channel columns
 *   (marketplace + provider-owned), a country-overrides count column,
 *   and an actions column. Percentages come LIVE from the canonical
 *   `/api/commission/preview` resolver; raw rule rows come from
 *   `/api/commission/rules`.
 *
 *   The simplified Edit drawer maps to the canonical backend schema:
 *     channel · package_id · country · commission_value (bps)
 *
 *   STRICT MODE: NO backend changes, NO engine changes, NO BPS exposed
 *   in the simplified tab, NO rule_id / priority / resolution_chain
 *   exposed in the simplified tab. The Advanced tab keeps full
 *   diagnostics.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import {
  Percent, Plus, Edit2, Trash2, FlaskConical, Loader2,
  ArrowLeft, Check, ChevronDown, Info, Globe,
} from 'lucide-react';
import api from '../../utils/api';
import { usePackageCatalogOptional } from '../../context/PackageCatalogContext';
import CommissionVatPanel from './CommissionVatPanel';

// ── PXC-B.3 feature flag ─────────────────────────────────────────────
// true  → default to the new Simplified matrix tab (Advanced still available)
// false → default to Advanced (legacy console), rollback path
const PXC_B3_SIMPLIFIED_MATRIX_DEFAULT = true;

// ── Constants ────────────────────────────────────────────────────────
// Canonical channels surfaced in the simplified tab. The Advanced tab
// preserves legacy `zero_commission` / `custom` mode strings.
const CANONICAL_CHANNELS = [
  {
    value: 'marketplace',
    label: 'Marketplace patients',
    short: 'Marketplace',
    helper: 'When TreatX brings the patient.',
  },
  {
    value: 'provider_owned',
    label: 'Provider-owned patients',
    short: 'Provider-owned',
    helper: 'When the clinic brings its own patient but uses TreatX infrastructure.',
  },
];

// TREATX-P0-1-SSOT: Package tier list is NEVER hardcoded. The
// rendered list, ordering, and editor selector are ALL derived at
// runtime from the canonical `packages` collection via
// `usePackageCatalog()` → `GET /api/subscription/packages`. If Ops
// adds a "Business" or "Enterprise+" package, this page picks it up
// without a single line of code change. If the catalog has not
// finished loading yet, the matrix renders empty (never a hardcoded
// fallback).
const orderPackageKeys = (packages) => {
  if (!Array.isArray(packages)) return [];
  return [...packages]
    .sort((a, b) => {
      const ao = typeof a?.upgrade_order === 'number' ? a.upgrade_order : 9999;
      const bo = typeof b?.upgrade_order === 'number' ? b.upgrade_order : 9999;
      if (ao !== bo) return ao - bo;
      // TREATX-COMMISSION-UX-CLARITY-01 — sort by the canonical key
      // surfaced by the catalog (`id`), falling back to `package_key`
      // for forward-compat if the API ever exposes that field.
      const ak = String(a?.id || a?.package_key || '');
      const bk = String(b?.id || b?.package_key || '');
      return ak.localeCompare(bk);
    })
    // TREATX-COMMISSION-UX-CLARITY-01 — `PackageCatalogContext.transformApiPackage`
    // returns the canonical package identifier on `id` (e.g. 'TRIAL',
    // 'STARTER', 'PRO', 'ENTERPRISE'); the legacy code here read the
    // never-populated `package_key` and produced an empty
    // `packageKeys` array, which silently dropped TRIAL from the
    // Simplified Matrix because TRIAL has no rule in
    // `commission_rules` for the fallback `extras` path to recover
    // it. Read `id` first, fall back to `package_key` for forward
    // compatibility. No new SSOT, no hardcoded fallback.
    .map((p) => p?.id || p?.package_key)
    .filter(Boolean);
};

const LEGACY_RULE_TYPES = [
  { value: 'platform_default', label: 'Platform Default', priority: 0 },
  { value: 'treatment_category', label: 'Treatment Category', priority: 10 },
  { value: 'country', label: 'Country / Market', priority: 20 },
  { value: 'package', label: 'Package', priority: 25 },
  { value: 'clinic', label: 'Clinic / Provider', priority: 30 },
  { value: 'campaign', label: 'Campaign', priority: 40 },
  { value: 'lead_source', label: 'Lead Source', priority: 50 },
  { value: 'legal_entity', label: 'Legal Entity', priority: 60 },
];

const LEGACY_MODE_OPTIONS = [
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'provider_owned', label: 'Provider-owned' },
  { value: 'zero_commission', label: 'Zero Commission' },
  { value: 'custom', label: 'Custom' },
];

// ── Helpers ──────────────────────────────────────────────────────────
const bpsToPercent = (bps) => {
  if (typeof bps !== 'number') return '—';
  return bps % 100 === 0 ? (bps / 100).toFixed(0) : (bps / 100).toFixed(1);
};

const percentToBps = (pct) => {
  if (pct === null || pct === undefined || pct === '') return null;
  const n = Number(pct);
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100);
};

// ── Simplified: Edit drawer ──────────────────────────────────────────
const SimplifiedEditDialog = ({ open, onClose, onSave, draft, saving, packageKeys }) => {
  const [pct, setPct] = useState('');
  const [channel, setChannel] = useState('marketplace');
  const [country, setCountry] = useState('');
  const [pkg, setPkg] = useState('');

  useEffect(() => {
    if (!draft) return;
    setPct(typeof draft.commission_value === 'number' ? bpsToPercent(draft.commission_value) : '');
    setChannel(draft.channel || 'marketplace');
    setCountry(draft.country || '');
    setPkg(draft.package_id || '');
  }, [draft, open]);

  const disabled =
    saving ||
    !pkg ||
    !channel ||
    pct === '' ||
    pct === null ||
    Number.isNaN(Number(pct)) ||
    Number(pct) < 0 ||
    Number(pct) > 100;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="simplified-edit-dialog">
        <DialogHeader>
          <DialogTitle>
            {draft?.rule_id ? 'Edit commission rate' : 'Create commission rate'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Provider plan</Label>
            <Select value={pkg} onValueChange={setPkg} disabled={!!draft?.rule_id}>
              <SelectTrigger data-testid="simplified-edit-package"><SelectValue placeholder="Select package" /></SelectTrigger>
              <SelectContent>
                {(packageKeys || []).map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger data-testid="simplified-edit-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CANONICAL_CHANNELS.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`simplified-channel-option-${c.value}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              {CANONICAL_CHANNELS.find((c) => c.value === channel)?.helper}
            </p>
          </div>
          <div>
            <Label>Country override (optional)</Label>
            <Input
              data-testid="simplified-edit-country"
              value={country}
              placeholder="Leave blank for global default"
              onChange={(e) => setCountry(e.target.value)}
            />
          </div>
          <div>
            <Label>Commission rate</Label>
            <div className="flex items-center gap-2">
              <Input
                data-testid="simplified-edit-pct"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                className="max-w-[120px]"
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Stored as the canonical bps internally; you only see the percentage here.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="simplified-edit-cancel">
            Cancel
          </Button>
          <Button
            data-testid="simplified-edit-save"
            onClick={() => onSave({
              rule_id: draft?.rule_id || null,
              channel,
              package_id: pkg,
              country: country.trim() || null,
              commission_value: percentToBps(pct),
            })}
            disabled={disabled}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            {draft?.rule_id ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Simplified: Country overrides drawer ─────────────────────────────
const OverridesDrawer = ({ open, onClose, rows, packageId, channel, onEdit, onDeactivate }) => {
  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md" data-testid="overrides-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Globe className="h-4 w-4" /> Country overrides
            {packageId && (
              <Badge variant="outline" className="ml-1 text-xs">{packageId}</Badge>
            )}
            {channel && (
              <Badge variant="outline" className="text-xs">{CANONICAL_CHANNELS.find((c) => c.value === channel)?.short || channel}</Badge>
            )}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-2">
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="overrides-empty">
              No country overrides for this combination.
            </p>
          ) : (
            rows.map((r) => (
              <div
                key={r.rule_id}
                className="border rounded-md px-3 py-2 flex items-start justify-between gap-3"
                data-testid={`override-row-${r.rule_id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {r.country || 'Global'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {r.country
                      ? `${r.country} override replaces default ${r.package_id || 'all-packages'} ${CANONICAL_CHANNELS.find((c) => c.value === r.channel)?.short || r.channel} rate.`
                      : 'Applies platform-wide.'}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-base font-semibold tabular-nums" data-testid={`override-pct-${r.rule_id}`}>
                    {bpsToPercent(r.commission_value)}%
                  </span>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(r)} data-testid={`override-edit-${r.rule_id}`}>
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => onDeactivate(r.rule_id)} data-testid={`override-deactivate-${r.rule_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ── Simplified: Matrix tab ───────────────────────────────────────────
const SimplifiedMatrix = ({ rules, onRulesChange, onCreate, onEditRule, onDeactivateRule, packageKeys }) => {
  const [previewByPkg, setPreviewByPkg] = useState({});
  const [loadingPreview, setLoadingPreview] = useState(true);

  // TREATX-P0-1-SSOT: Distinct package_ids come ONLY from the live
  // packages catalog (`packageKeys`). Any legacy `package_id` that
  // still has a row in `commission_rules` is appended at the end of
  // the matrix so the rule remains editable — but the order and
  // primary list are always anchored to the canonical SSOT.
  const packageIds = useMemo(() => {
    const base = Array.isArray(packageKeys) ? packageKeys : [];
    const seen = new Set(base);
    const extras = [];
    rules.forEach((r) => {
      if (r.package_id && !seen.has(r.package_id)) {
        seen.add(r.package_id);
        extras.push(r.package_id);
      }
    });
    return [...base, ...extras];
  }, [rules, packageKeys]);

  useEffect(() => {
    let cancelled = false;
    setLoadingPreview(true);
    const fetchOne = async (pkg, channel) => {
      try {
        const { data } = await api.get('/commission/preview', {
          params: { channel, package_id: pkg, amount: 0 },
        });
        if (!data?.success) return null;
        return typeof data.commission_value === 'number' ? data.commission_value : null;
      } catch (e) {
        return null;
      }
    };
    (async () => {
      const next = {};
      await Promise.all(
        packageIds.map(async (pkg) => {
          const [mp, po] = await Promise.all([
            fetchOne(pkg, 'marketplace'),
            fetchOne(pkg, 'provider_owned'),
          ]);
          next[pkg] = { marketplace: mp, provider_owned: po };
        })
      );
      if (!cancelled) {
        setPreviewByPkg(next);
        setLoadingPreview(false);
      }
    })();
    return () => { cancelled = true; };
  }, [packageIds.join('|'), rules.length]);

  // Count country overrides per (package, channel).
  // A country override = active rule where `country != null` AND
  // (package_id === pkg OR package_id is null/empty).
  const overrideRowsFor = useCallback((pkg, channel) => {
    return rules.filter(
      (r) =>
        r.active !== false &&
        r.country &&
        r.channel === channel &&
        (r.package_id === pkg || !r.package_id),
    );
  }, [rules]);

  // TREATX-COMMISSION-UX-CLARITY-01 — does this package have ANY
  // explicit override row in the commission_rules collection? Used
  // to surface a "Uses Global Default" badge for packages (e.g.
  // TRIAL) that intentionally inherit the global default rate at
  // resolve-time. We check for active package-level rows on either
  // channel (marketplace or provider_owned). Country / campaign
  // rows are NOT counted as a "package override" — only rows
  // whose package_id matches the package itself. Resolved
  // percentages are STILL sourced from the existing
  // /api/commission/preview API; this badge is purely informational.
  const usesGlobalDefault = useCallback((pkg) => {
    return !rules.some(
      (r) =>
        r.active !== false &&
        r.package_id === pkg &&
        !r.country &&
        !r.provider_id &&
        !r.campaign_id,
    );
  }, [rules]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPkg, setDrawerPkg] = useState('');
  const [drawerChannel, setDrawerChannel] = useState('');

  const openDrawer = (pkg, channel) => {
    setDrawerPkg(pkg);
    setDrawerChannel(channel);
    setDrawerOpen(true);
  };

  return (
    <div className="space-y-3" data-testid="simplified-matrix">
      {/* Helper banner */}
      <div className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50/60 border border-slate-200 rounded-md px-3 py-2">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          Live rates from the commission engine. Each package can charge a different commission for marketplace patients
          (TreatX-sourced) vs provider-owned patients (clinic-sourced). Country overrides replace the package default
          for matching transactions.
        </span>
      </div>

      {/* DESKTOP — table */}
      <div className="hidden md:block overflow-hidden border rounded-lg" data-testid="simplified-matrix-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Provider plan</th>
              <th className="text-right px-4 py-2 font-medium">
                Marketplace patients
                <div className="text-[10px] normal-case font-normal text-slate-400">When TreatX brings the patient</div>
              </th>
              <th className="text-right px-4 py-2 font-medium">
                Provider-owned patients
                <div className="text-[10px] normal-case font-normal text-slate-400">When the clinic brings the patient</div>
              </th>
              <th className="text-right px-4 py-2 font-medium">Country overrides</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {packageIds.map((pkg) => {
              const live = previewByPkg[pkg] || {};
              const mpOverrides = overrideRowsFor(pkg, 'marketplace');
              const poOverrides = overrideRowsFor(pkg, 'provider_owned');
              const totalOverrides = mpOverrides.length + poOverrides.length;
              return (
                <tr key={pkg} className="border-t hover:bg-slate-50/50" data-testid={`matrix-row-${pkg}`}>
                  <td className="px-4 py-3 font-medium text-slate-900" data-testid={`matrix-row-${pkg}-name`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{pkg}</span>
                      {usesGlobalDefault(pkg) && (
                        <Badge
                          variant="outline"
                          className="text-[10px] font-normal text-slate-500 border-slate-300"
                          data-testid={`matrix-row-${pkg}-uses-global-default`}
                          title="No package-level override. The commission engine falls back to the global default rule."
                        >
                          Uses Global Default
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums" data-testid={`matrix-row-${pkg}-marketplace`}>
                    {loadingPreview ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin inline" />
                    ) : live.marketplace !== null && live.marketplace !== undefined ? (
                      <span className="font-semibold text-slate-900">{bpsToPercent(live.marketplace)}%</span>
                    ) : (
                      <span className="text-slate-400">Not configured</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums" data-testid={`matrix-row-${pkg}-provider-owned`}>
                    {loadingPreview ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin inline" />
                    ) : live.provider_owned !== null && live.provider_owned !== undefined ? (
                      <span className="font-semibold text-slate-900">{bpsToPercent(live.provider_owned)}%</span>
                    ) : (
                      <span className="text-slate-400">Not configured</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right" data-testid={`matrix-row-${pkg}-overrides`}>
                    {totalOverrides === 0 ? (
                      <span className="text-slate-400 text-xs">None</span>
                    ) : (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => openDrawer(pkg, mpOverrides.length >= poOverrides.length ? 'marketplace' : 'provider_owned')}
                        data-testid={`matrix-row-${pkg}-overrides-btn`}
                      >
                        {totalOverrides} {totalOverrides === 1 ? 'country override' : 'country overrides'}
                      </Button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onEditRule({ package_id: pkg, channel: 'marketplace' })}
                      data-testid={`matrix-row-${pkg}-edit-btn`}
                    >
                      <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MOBILE — stacked cards */}
      <div className="md:hidden space-y-2" data-testid="simplified-matrix-cards">
        {packageIds.map((pkg) => {
          const live = previewByPkg[pkg] || {};
          const mpOverrides = overrideRowsFor(pkg, 'marketplace');
          const poOverrides = overrideRowsFor(pkg, 'provider_owned');
          const totalOverrides = mpOverrides.length + poOverrides.length;
          return (
            <div key={pkg} className="border rounded-lg p-3 space-y-2" data-testid={`matrix-card-${pkg}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="font-semibold text-slate-900">{pkg}</span>
                  {usesGlobalDefault(pkg) && (
                    <Badge
                      variant="outline"
                      className="text-[10px] font-normal text-slate-500 border-slate-300"
                      data-testid={`matrix-card-${pkg}-uses-global-default`}
                      title="No package-level override. The commission engine falls back to the global default rule."
                    >
                      Uses Global Default
                    </Badge>
                  )}
                </div>
                <Button size="sm" variant="ghost" onClick={() => onEditRule({ package_id: pkg, channel: 'marketplace' })} data-testid={`matrix-card-${pkg}-edit-btn`}>
                  <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="border rounded-md px-2 py-1.5 bg-slate-50/60" data-testid={`matrix-card-${pkg}-marketplace`}>
                  <div className="text-[10px] text-slate-500">Marketplace</div>
                  <div className="text-base font-semibold tabular-nums">
                    {loadingPreview ? '…' : (live.marketplace !== null && live.marketplace !== undefined ? `${bpsToPercent(live.marketplace)}%` : '—')}
                  </div>
                </div>
                <div className="border rounded-md px-2 py-1.5 bg-slate-50/60" data-testid={`matrix-card-${pkg}-provider-owned`}>
                  <div className="text-[10px] text-slate-500">Provider-owned</div>
                  <div className="text-base font-semibold tabular-nums">
                    {loadingPreview ? '…' : (live.provider_owned !== null && live.provider_owned !== undefined ? `${bpsToPercent(live.provider_owned)}%` : '—')}
                  </div>
                </div>
              </div>
              {totalOverrides > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => openDrawer(pkg, mpOverrides.length >= poOverrides.length ? 'marketplace' : 'provider_owned')}
                  data-testid={`matrix-card-${pkg}-overrides-btn`}
                >
                  {totalOverrides} country {totalOverrides === 1 ? 'override' : 'overrides'}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Create button */}
      <div className="flex justify-end">
        <Button onClick={onCreate} data-testid="simplified-create-btn">
          <Plus className="h-4 w-4 mr-1" /> New commission rate
        </Button>
      </div>

      <OverridesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        rows={[
          ...overrideRowsFor(drawerPkg, 'marketplace'),
          ...overrideRowsFor(drawerPkg, 'provider_owned'),
        ]}
        packageId={drawerPkg}
        channel={drawerChannel}
        onEdit={(r) => onEditRule(r)}
        onDeactivate={(rid) => {
          onDeactivateRule(rid);
          // optimistic close so caller refreshes
          setDrawerOpen(false);
        }}
      />
    </div>
  );
};

// ── Legacy / Advanced — rule row ─────────────────────────────────────
function LegacyRuleRow({ rule, onEdit, onDeactivate }) {
  // Use either the legacy `commission_rate_bps` or the canonical
  // `commission_value` field — whichever the rule carries.
  const bps =
    typeof rule.commission_value === 'number'
      ? rule.commission_value
      : typeof rule.commission_rate_bps === 'number'
      ? rule.commission_rate_bps
      : null;
  const typeInfo = rule.rule_type
    ? LEGACY_RULE_TYPES.find((t) => t.value === rule.rule_type) || { label: rule.rule_type }
    : { label: rule.channel || 'rule' };
  const target = rule.target_id || rule.package_id || rule.country || rule.provider_id || 'global';
  return (
    <div
      data-testid={`commission-rule-${rule.rule_id}`}
      className="flex items-center gap-3 px-4 py-3 border rounded-lg hover:bg-muted/30 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{typeInfo.label}</span>
          <Badge variant="outline" className="text-xs font-mono" data-testid={`rule-target-${rule.rule_id}`}>
            {target === '*' ? 'All' : target}
          </Badge>
          {rule.channel && (
            <Badge variant="secondary" className="text-xs capitalize">{rule.channel}</Badge>
          )}
        </div>
        {rule.description && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{rule.description}</p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <span className="text-lg font-semibold tabular-nums" data-testid={`rule-rate-${rule.rule_id}`}>
          {bpsToPercent(bps)}%
        </span>
        <div className="text-xs text-muted-foreground">P{rule.priority ?? 0} · {bps} bps</div>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(rule)} data-testid={`edit-rule-${rule.rule_id}`}>
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => onDeactivate(rule.rule_id)} data-testid={`deactivate-rule-${rule.rule_id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ── Legacy / Advanced — rule form dialog ─────────────────────────────
function LegacyRuleFormDialog({ open, onClose, onSave, editRule, saving }) {
  const [form, setForm] = useState({
    rule_type: 'country',
    target_id: '',
    commission_rate_bps: 1500,
    commission_mode: 'marketplace',
    priority: 20,
    description: '',
  });

  useEffect(() => {
    if (editRule) {
      setForm({
        rule_type: editRule.rule_type || 'country',
        target_id: editRule.target_id || editRule.country || editRule.package_id || '',
        commission_rate_bps:
          typeof editRule.commission_rate_bps === 'number'
            ? editRule.commission_rate_bps
            : typeof editRule.commission_value === 'number'
            ? editRule.commission_value
            : 1500,
        commission_mode: editRule.commission_mode || editRule.channel || 'marketplace',
        priority: editRule.priority || 10,
        description: editRule.description || '',
      });
    } else {
      setForm({
        rule_type: 'country',
        target_id: '',
        commission_rate_bps: 1500,
        commission_mode: 'marketplace',
        priority: 20,
        description: '',
      });
    }
  }, [editRule, open]);

  const handleTypeChange = (val) => {
    const typeInfo = LEGACY_RULE_TYPES.find((t) => t.value === val);
    setForm((f) => ({
      ...f,
      rule_type: val,
      priority: typeInfo?.priority || f.priority,
      target_id: val === 'platform_default' ? '*' : f.target_id,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="commission-rule-dialog">
        <DialogHeader>
          <DialogTitle>{editRule ? 'Edit Commission Rule' : 'Create Commission Rule'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Rule Type</Label>
            <Select value={form.rule_type} onValueChange={handleTypeChange} disabled={!!editRule}>
              <SelectTrigger data-testid="rule-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEGACY_RULE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Target ID</Label>
            <Input
              data-testid="rule-target-input"
              value={form.target_id}
              onChange={(e) => setForm((f) => ({ ...f, target_id: e.target.value }))}
              placeholder={form.rule_type === 'country' ? 'e.g. Turkey' : form.rule_type === 'clinic' ? 'clinic-id' : form.rule_type === 'package' ? 'package-id' : 'target value'}
              disabled={form.rule_type === 'platform_default'}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Rate (BPS)</Label>
              <Input
                data-testid="rule-rate-input"
                type="number"
                value={form.commission_rate_bps}
                onChange={(e) => setForm((f) => ({ ...f, commission_rate_bps: parseInt(e.target.value, 10) || 0 }))}
              />
              <span className="text-xs text-muted-foreground">{bpsToPercent(form.commission_rate_bps)}%</span>
            </div>
            <div>
              <Label>Priority</Label>
              <Input
                data-testid="rule-priority-input"
                type="number"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: parseInt(e.target.value, 10) || 0 }))}
              />
              <span className="text-xs text-muted-foreground">Higher = more specific</span>
            </div>
          </div>
          <div>
            <Label>Commission Mode (channel)</Label>
            <Select value={form.commission_mode} onValueChange={(v) => setForm((f) => ({ ...f, commission_mode: v }))}>
              <SelectTrigger data-testid="rule-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEGACY_MODE_OPTIONS.map((m) => (
                  <SelectItem key={m.value} value={m.value} data-testid={`rule-mode-option-${m.value}`}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description</Label>
            <Input
              data-testid="rule-description-input"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional description"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            data-testid="save-rule-btn"
            onClick={() => onSave(form, editRule?.rule_id)}
            disabled={saving || (!form.target_id && form.rule_type !== 'platform_default')}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            {editRule ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Public component ─────────────────────────────────────────────────
export default function AdminCommissionRules() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // TREATX-P0-1-SSOT: package tier list = live `packages` collection.
  // No hardcoded array. If catalog fails / not yet loaded, the matrix
  // renders zero rows and the editor's package selector is empty —
  // never a hardcoded fallback.
  const { getAllPackages } = usePackageCatalogOptional();
  const packageKeys = useMemo(
    () => orderPackageKeys(getAllPackages() || []),
    [getAllPackages],
  );

  // Tab state — persisted via ?tab=
  const initialTab =
    searchParams.get('tab') ||
    (PXC_B3_SIMPLIFIED_MATRIX_DEFAULT ? 'simplified' : 'advanced');
  const [activeTab, setActiveTab] = useState(initialTab);
  const handleTabChange = (v) => {
    setActiveTab(v);
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', v);
    setSearchParams(sp, { replace: true });
  };
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && t !== activeTab) setActiveTab(t);
  }, [searchParams, activeTab]);

  // Simplified state
  const [simplifiedDialogOpen, setSimplifiedDialogOpen] = useState(false);
  const [simplifiedDraft, setSimplifiedDraft] = useState(null);

  // Legacy state (advanced tab) — preserved
  const [legacyDialogOpen, setLegacyDialogOpen] = useState(false);
  const [legacyEditRule, setLegacyEditRule] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInput, setPreviewInput] = useState({
    clinic_id: '', country: '', treatment_category: '',
    package_id: '', lead_source: '', campaign_id: '',
    legal_entity_id: '', explicit_override_bps: '',
  });
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/commission/rules?active_only=false`);
      const data = res.data;
      setRules((data.rules || []).sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)));
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  // Simplified-tab save → canonical schema
  const handleSimplifiedSave = async (draft) => {
    setSaving(true);
    try {
      const body = {
        channel: draft.channel,
        package_id: draft.package_id || null,
        country: draft.country || null,
        commission_value: draft.commission_value,
        commission_type: 'percentage',
      };
      if (draft.rule_id) {
        await api.put(`/commission/rules/${draft.rule_id}`, body);
      } else {
        await api.post(`/commission/rules`, body);
      }
      setSimplifiedDialogOpen(false);
      setSimplifiedDraft(null);
      await fetchRules();
    } catch { /* surface via console only */ }
    setSaving(false);
  };

  // Legacy-tab save (untouched contract)
  const handleLegacySave = async (form, ruleId) => {
    setSaving(true);
    try {
      if (ruleId) {
        await api.put(`/commission/rules/${ruleId}`, form);
      } else {
        await api.post(`/commission/rules`, form);
      }
      setLegacyDialogOpen(false);
      setLegacyEditRule(null);
      await fetchRules();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleDeactivate = async (ruleId) => {
    if (!window.confirm('Deactivate this commission rule?')) return;
    try {
      await api.delete(`/commission/rules/${ruleId}`);
      await fetchRules();
    } catch { /* ignore */ }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const body = { ...previewInput };
      if (body.explicit_override_bps === '' || body.explicit_override_bps === null) {
        delete body.explicit_override_bps;
      } else {
        body.explicit_override_bps = parseInt(body.explicit_override_bps, 10);
      }
      Object.keys(body).forEach((k) => { if (body[k] === '') delete body[k]; });
      const res = await api.post(`/commission/resolve`, body);
      setPreviewResult(res.data);
    } catch { setPreviewResult(null); }
    setPreviewLoading(false);
  };

  const openSimplifiedCreate = () => {
    setSimplifiedDraft({
      rule_id: null,
      channel: 'marketplace',
      // PKG-CATALOG-CREATE-01 hygiene: no package-specific default. Start with
      // no selection so the Provider Plan selector (dynamically derived from the
      // live canonical catalog) drives the choice — new packages appear here
      // automatically with no code change.
      package_id: null,
      country: null,
      commission_value: 1500,
    });
    setSimplifiedDialogOpen(true);
  };

  const openSimplifiedEdit = (rule) => {
    setSimplifiedDraft({
      rule_id: rule.rule_id || null,
      channel: rule.channel || 'marketplace',
      package_id: rule.package_id || '',
      country: rule.country || null,
      commission_value:
        typeof rule.commission_value === 'number'
          ? rule.commission_value
          : typeof rule.commission_rate_bps === 'number'
          ? rule.commission_rate_bps
          : 1500,
    });
    setSimplifiedDialogOpen(true);
  };

  return (
    <div data-testid="admin-commission-page" className="max-w-5xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate('/treatx-admin/dashboard')} data-testid="commission-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="commission-page-title">
              <Percent className="h-6 w-6" /> Commission Management
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage marketplace and provider-owned commission per provider plan.
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-4" data-testid="commission-tabs-list">
          <TabsTrigger value="simplified" data-testid="commission-tab-simplified">
            Simplified
          </TabsTrigger>
          {/* TREATX-COMMISSION-UX-CLARITY-01 — internal tab value is
              kept as 'advanced' so deep links and ?tab=advanced URLs
              still work. The label users see is "Override Rules"
              because this tab lists only explicit override rows from
              the commission_rules collection. Packages without an
              override entry (e.g. TRIAL) inherit the Global Default
              and are visible in the Simplified tab. */}
          <TabsTrigger value="advanced" data-testid="commission-tab-advanced">
            Override Rules
          </TabsTrigger>
          {/* TREATX-TAX-3 / TAX-4 — commission VAT policy lives in the same
              screen as the commission rules it taxes. */}
          <TabsTrigger value="vat" data-testid="commission-tab-vat">
            Commission VAT
          </TabsTrigger>
        </TabsList>

        {/* SIMPLIFIED */}
        <TabsContent value="simplified" data-testid="commission-tab-simplified-content">
          {loading ? (
            <Card><CardContent className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></CardContent></Card>
          ) : (
            <SimplifiedMatrix
              rules={rules}
              onRulesChange={fetchRules}
              onCreate={openSimplifiedCreate}
              onEditRule={openSimplifiedEdit}
              onDeactivateRule={handleDeactivate}
              packageKeys={packageKeys}
            />
          )}
        </TabsContent>

        {/* ADVANCED — legacy console preserved untouched */}
        <TabsContent value="advanced" data-testid="commission-tab-advanced-content">
          {/* TREATX-COMMISSION-UX-CLARITY-01 — help banner. Packages
              with no explicit override row inherit the Global Default
              at resolve-time (see commission_engine.resolve_commission
              precedence: campaign → package → provider → country →
              global). This list shows only the rows in the
              commission_rules collection — no implicit "uses global
              default" rows are synthesised. */}
          <div
            className="flex items-start gap-2 text-xs text-slate-600 bg-blue-50/60 border border-blue-200 rounded-md px-3 py-2 mb-3"
            data-testid="override-rules-help-banner"
          >
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-blue-600" />
            <span>
              This page lists only explicit commission override rules.
              Packages without overrides inherit the Global Default commission.
            </span>
          </div>
          <div className="flex justify-end mb-3">
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPreviewOpen(!previewOpen)} data-testid="toggle-preview-btn">
                <FlaskConical className="h-4 w-4 mr-1" />
                {previewOpen ? 'Hide' : 'Preview'}
              </Button>
              <Button
                onClick={() => { setLegacyEditRule(null); setLegacyDialogOpen(true); }}
                data-testid="create-rule-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> New Rule
              </Button>
            </div>
          </div>

          {previewOpen && (
            <Card data-testid="commission-preview-panel" className="border-blue-200 bg-blue-50/30 mb-3">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FlaskConical className="h-4 w-4" /> Resolution Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { key: 'clinic_id', label: 'Clinic ID' },
                    { key: 'country', label: 'Country' },
                    { key: 'treatment_category', label: 'Treatment' },
                    { key: 'package_id', label: 'Package ID' },
                    { key: 'lead_source', label: 'Lead Source' },
                    { key: 'campaign_id', label: 'Campaign' },
                    { key: 'legal_entity_id', label: 'Legal Entity' },
                    { key: 'explicit_override_bps', label: 'Override (BPS)' },
                  ].map((f) => (
                    <div key={f.key}>
                      <Label className="text-xs">{f.label}</Label>
                      <Input
                        data-testid={`preview-${f.key}`}
                        className="h-8 text-xs"
                        value={previewInput[f.key]}
                        onChange={(e) => setPreviewInput((p) => ({ ...p, [f.key]: e.target.value }))}
                        placeholder={f.label}
                      />
                    </div>
                  ))}
                </div>
                <Button size="sm" onClick={handlePreview} disabled={previewLoading} data-testid="run-preview-btn">
                  {previewLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FlaskConical className="h-3.5 w-3.5 mr-1" />}
                  Resolve
                </Button>
                {previewResult && (
                  <div data-testid="preview-result" className="p-3 rounded-md bg-white border text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Rate</span>
                      <span className="font-semibold" data-testid="preview-rate">
                        {previewResult.commission_rate_bps ?? previewResult.commission_value} BPS ({bpsToPercent(previewResult.commission_rate_bps ?? previewResult.commission_value)}%)
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Mode</span>
                      <span className="capitalize">{previewResult.commission_mode || previewResult.channel}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Resolved By</span>
                      <Badge variant="outline" className="text-xs" data-testid="preview-resolved-by">{previewResult.resolved_by}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Rule ID</span>
                      <span className="text-xs font-mono">{previewResult.rule_id}</span>
                    </div>
                    {previewResult.resolution_chain?.length > 0 && (
                      <div className="pt-2 border-t">
                        <span className="text-xs text-muted-foreground">Resolution Chain ({previewResult.resolution_chain.length} rules matched):</span>
                        {previewResult.resolution_chain.map((r, i) => (
                          <div key={i} className="text-xs flex items-center gap-1 mt-0.5">
                            <ChevronDown className="h-3 w-3 text-muted-foreground" />
                            <span className="capitalize">{r.rule_type || r.channel}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="font-mono">{r.commission_rate_bps ?? r.commission_value} BPS</span>
                            <span className="text-muted-foreground">(P{r.priority})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Active Rules
                <Badge variant="outline" className="ml-2">{rules.filter((r) => r.active !== false).length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : rules.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No commission rules defined yet</p>
              ) : (
                <div className="space-y-2" data-testid="commission-rules-list">
                  {rules.filter((r) => r.active !== false).map((rule) => (
                    <LegacyRuleRow
                      key={rule.rule_id}
                      rule={rule}
                      onEdit={(r) => { setLegacyEditRule(r); setLegacyDialogOpen(true); }}
                      onDeactivate={handleDeactivate}
                    />
                  ))}
                  {rules.some((r) => r.active === false) && (
                    <>
                      <div className="text-xs text-muted-foreground pt-3 border-t">
                        Deactivated ({rules.filter((r) => r.active === false).length})
                      </div>
                      {rules.filter((r) => r.active === false).map((rule) => (
                        <div key={rule.rule_id} className="opacity-50">
                          <LegacyRuleRow rule={rule} onEdit={(r) => { setLegacyEditRule(r); setLegacyDialogOpen(true); }} onDeactivate={() => {}} />
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <LegacyRuleFormDialog
            open={legacyDialogOpen}
            onClose={() => { setLegacyDialogOpen(false); setLegacyEditRule(null); }}
            onSave={handleLegacySave}
            editRule={legacyEditRule}
            saving={saving}
          />
        </TabsContent>

        {/* TREATX-TAX-3 / TAX-4 — Commission VAT policy */}
        <TabsContent value="vat" data-testid="commission-tab-vat-content">
          <CommissionVatPanel />
        </TabsContent>

      </Tabs>

      <SimplifiedEditDialog
        open={simplifiedDialogOpen}
        onClose={() => { setSimplifiedDialogOpen(false); setSimplifiedDraft(null); }}
        onSave={handleSimplifiedSave}
        draft={simplifiedDraft}
        saving={saving}
        packageKeys={packageKeys}
      />
    </div>
  );
}

